import axios from "axios"
import {createApi, fetchBaseQuery} from "@reduxjs/toolkit/query/react"
import type {AcapulcoProperty, PropertyType, RegionCode} from "@casas-diamante/types"
import {API_URL} from "../constants"
import {parseBooleanFilter} from "../api/utils"

// Set to use the credentials for AUTH
axios.defaults.withCredentials = true
const URL_PREFIX = `${API_URL}/properties`

type PropertyCategoryPriceComparison = {
   percentDifference: number
   direction: "below" | "above" | "equal"
   status: "neutral" | "belowAverage" | "wellBelowAverage" | "aboveAverage"
}

export type PropertyCategoryPriceMetricsResponse = {
   averagePricePerMeter: number | null
   averagePropertyCount: number
   categoryLabel: string
   typeAverages: {
      propertyType: PropertyType
      propertyTypeLabel: string
      averagePricePerMeter: number
      propertyCountUsed: number
   }[]
   comparison: PropertyCategoryPriceComparison | null
}

export type PropertyPriceAnalysisRow = {
   key: "category" | "condo" | "property"
   label: string
   pricePerMeter: number | null
   count: number | null
   countLabel: string | null
   comparison: {
      percentDifference: number
      direction: "below" | "above" | "equal"
      reference: "category" | "condo"
   } | null
}

export type PropertyPriceAnalysisResponse = {
   rows: PropertyPriceAnalysisRow[]
   generatedAt: string
}

export const propertiesRtkApi = createApi({
   reducerPath: "propertiesApi",
   baseQuery: fetchBaseQuery({
      baseUrl: URL_PREFIX,
      credentials: "include",
   }),
   tagTypes: ["PropertyPriceAnalysis"],
   keepUnusedDataFor: 300,
   refetchOnMountOrArgChange: 300,
   endpoints: (builder) => ({
      getPropertyPriceAnalysis: builder.query<PropertyPriceAnalysisResponse, string>({
         query: (id) => `/inmueble/${id}/price-analysis`,
         providesTags: ["PropertyPriceAnalysis"],
      }),
   }),
})

export const {useGetPropertyPriceAnalysisQuery} = propertiesRtkApi

export const getInmuebleById = async (id: string) =>
   (await axios.get(`${URL_PREFIX}/inmueble/${id}`)).data

export const downloadPropertyReport = async (id: string, scheme?: "sale" | "rent") =>
   axios.get(`${URL_PREFIX}/inmueble/${id}/report.pdf`, {
      params: scheme ? {scheme} : undefined,
      responseType: "blob",
   })

export const getPropertyCategoryPriceMetrics = async (id: string) =>
   (await axios.get(`${URL_PREFIX}/inmueble/${id}/category-price-metrics`))
      .data as PropertyCategoryPriceMetricsResponse

export const getTemporadas = async () =>
   (
      await axios({
         method: "get",
         url: `${API_URL}/temporadas`,
      })
   ).data?.data

export const getPartnersProperties = async (
   region: RegionCode,
   searchQuery: string,
   partners: string[],
   suspended: string | null,
   available: string | null,
   status: string[],
   page: number,
   condoClass: string[],
) => {
   const response = await axios({
      method: "post",
      url: `${URL_PREFIX}/socios`,
      data: {
         region,
         searchQuery,
         partners,
         suspended: parseBooleanFilter(suspended),
         available: parseBooleanFilter(available),
         status,
         page,
         condoClass,
      },
      headers: {
         "Content-Type": "application/json",
      },
   })

   return response
}

export const postProperty = async (property: AcapulcoProperty) => {
   const resPost = await axios({
      method: "POST",
      url: URL_PREFIX,
      data: property,
      headers: {
         "Content-Type": "application/json",
      },
   })
   return resPost.data
}

// order images of the inmueble
export const orderImages = async (id: string, images: any, inmueble: any) => {
   await axios({
      method: "PUT",
      url: `${URL_PREFIX}/inmueble/orderImages`,
      data: {id: id, images: images, inmueble: inmueble},
      headers: {
         "Content-Type": "application/json",
      },
   })
}

export const uploadPropertyImages = async (formData: FormData, id: any) => {
   const resPut = await axios({
      method: "put",
      url: `${URL_PREFIX}/inmueble/images/?id=${id}`,
      data: formData,
      headers: {
         "Content-Type": "multipart/form-data; charset=utf-8",
      },
   })
   return resPut
}

// edit inmueble
export const editInmueble = async (inmuebleUpdated: any) => {
   await axios({
      method: "put",
      url: `${URL_PREFIX}/inmueble/data/`,
      data: inmuebleUpdated,
      headers: {
         "Content-Type": "application/json",
      },
   })
}

export type OwnerNotesUpdateResponse = {
   ownerNotes: string | null
}

export const updateOwnerNotes = async (
   id: string,
   ownerNotes: string | null,
): Promise<OwnerNotesUpdateResponse> =>
   (
      await axios.put<OwnerNotesUpdateResponse>(`${URL_PREFIX}/inmueble/${id}/owner-notes`, {
         ownerNotes,
      })
   ).data

// delete property endpoint
export const deleteProperty = async (id: string) => {
   const res = await axios({
      method: "delete",
      url: `${URL_PREFIX}/${id}`,
      headers: {
         "Content-Type": "application/json",
      },
   })
   return res
}

// order sections
export const orderSections = async (id: string, images: any, categoriesUpdated: any) => {
   await axios({
      method: "put",
      url: `${URL_PREFIX}/updateSections`,
      data: {
         id,
         orderedSections: categoriesUpdated,
         images,
      },
      headers: {
         "Content-Type": "application/json",
      },
   })
}

// main image changes
export const mainImageChanges = async (id: string, formData: FormData) => {
   await axios({
      method: "put",
      url: `${URL_PREFIX}/updateMainImage/${id}`,
      data: formData,
      headers: {
         "Content-Type": "multipart/form-data; charset=utf-8",
      },
   })
}

// add images
export const addImages = async (id: string, formData: FormData) => {
   await axios({
      method: "put",
      url: `${URL_PREFIX}/addImages/${id}`,
      data: formData,
      headers: {
         "Content-Type": "multipart/form-data; charset=utf-8",
      },
   })
}

// delete images
export const deleteImages = async (id: string, deletedImages: string[]) => {
   await axios({
      method: "delete",
      url: `${URL_PREFIX}/deleteImages/${id}`,
      data: {
         deletedImages,
      },
      headers: {
         "Content-Type": "application/json",
      },
   })
}

// delete section images
export const deleteSectionImages = async (
   id: string,
   categories: any,
   categoriesUpdated: any,
   images: any,
) => {
   const res = await axios({
      method: "delete",
      url: `${URL_PREFIX}/categoryImages`,
      data: {
         id,
         categories,
         categoriesUpdated,
         images,
      },
      headers: {
         "Content-Type": "application/json",
      },
   })
   return res.data.images
}

// delete main image
export const deleteMainImage = async (id: string, mainImage: string) => {
   await axios({
      method: "delete",
      url: `${URL_PREFIX}/mainImageDelete/${id}`,
      data: {
         mainImage,
      },
      headers: {
         "Content-Type": "application/json",
      },
   })
}

// delete date of the inmueble
export const deleteDate = async (id: string, date: string, calendar: any) => {
   await axios({
      method: "delete",
      url: `${URL_PREFIX}/calendarDate/${id}`,
      data: {
         date,
         calendar,
      },
      headers: {
         "Content-Type": "application/json",
      },
   })
}

// put date of the inmueble
export const putDate = async (id: string, calendar: any, calendarPast: any) => {
   await axios({
      method: "put",
      url: `${URL_PREFIX}/calendar/${id}`,
      data: {calendar, calendarPast},
      headers: {
         "Content-Type": "application/json",
      },
   })
}

// edit date
export const editDate = async (id: string, calendar: any, calendarPast: any, apiDate: any) => {
   await axios({
      method: "put",
      url: `${URL_PREFIX}/calendarDate/${id}`,
      data: {calendar, calendarPast, date: apiDate},
      headers: {
         "Content-Type": "application/json",
      },
   })
}

// upload new Images
export const uploadNewImages = async (id: string, images: any) => {
   const res = await axios({
      method: "put",
      url: `${URL_PREFIX}/uploadNewImages/${id}`,
      data: images,
      headers: {
         "Content-Type": "multipart/form-data; charset=utf-8",
      },
   })
   return res.data.filePaths
}

// get all inmuebles endpoint
export const getAllInmuebles = async () => {
   const res = await axios({
      method: "get",
      url: `${URL_PREFIX}/allInmuebles`,
      headers: {
         "Content-Type": "application/json",
      },
   })
   return res.data
}

//get properties recorridos
export type RecorridoFormData = {
   asesor: string
   cliente: string
   fecha: string
   comentarios?: string
   numero?: number
}

export const getRecorridosProperties = async (id: string) => {
   const res = await axios({
      method: "get",
      url: `${URL_PREFIX}/inmueble/${id}/recorridos`,
      headers: {
         "Content-Type": "application/json",
      },
   })
   return res.data
}

export const postRecorridosProperties = async (id: string, recorrido: RecorridoFormData) => {
   const res = await axios({
      method: "post",
      url: `${URL_PREFIX}/inmueble/${id}/recorridos`,
      data: recorrido,
      headers: {
         "Content-Type": "application/json",
      },
   })
   return res.data
}

export const putRecorridosProperties = async (
   id: string,
   recorridoId: string,
   recorrido: RecorridoFormData,
) => {
   const res = await axios({
      method: "put",
      url: `${URL_PREFIX}/inmueble/${id}/recorridos/${recorridoId}`,
      data: recorrido,
      headers: {
         "Content-Type": "application/json",
      },
   })
   return res.data
}

export const deleteRecorridosProperties = async (id: string, recorridoId: string) => {
   const res = await axios({
      method: "delete",
      url: `${URL_PREFIX}/inmueble/${id}/recorridos/${recorridoId}`,
      headers: {
         "Content-Type": "application/json",
      },
   })
   return res.data
}

export type PropertyReview = {
   id: string
   rating: number
   comment: string
   userEmail: string
   userName: string | null
   userPhotoURL: string | null
   createdAt?: unknown
   updatedAt?: unknown
}

export type PropertyReviewsResponse = {
   inmuebleId: string
   total: number
   averageRating: number
   reviewCount: number
   reviews: PropertyReview[]
}

export const getReviewsProperties = async (id: string): Promise<PropertyReviewsResponse> => {
   const res = await axios({
      method: "get",
      url: `${URL_PREFIX}/inmueble/${id}/reviews`,
      headers: {
         "Content-Type": "application/json",
      },
   })
   return res.data
}

export const deleteReviewProperty = async (id: string, reviewId: string) => {
   const res = await axios({
      method: "delete",
      url: `${URL_PREFIX}/inmueble/${id}/reviews/${encodeURIComponent(reviewId)}`,
      headers: {
         "Content-Type": "application/json",
      },
   })
   return res.data
}
